import { useState, useMemo } from "react";
import dayjs from "dayjs";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";

import TabList from "./TabList.jsx";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { formatDatePeriod } from "../services/formatService.js";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import "dayjs/locale/de";
import { typographyTokens } from "../theme/index.js";

dayjs.extend(quarterOfYear);

const PRESETS = {
  month: (start) => {
    start = start || dayjs();
    return {
      name: "Monat",
      start: start.startOf("month"),
      end: start.endOf("month"),
    };
  },
  quarter: (start) => {
    start = start || dayjs();
    return {
      name: "Quartal",
      start: start.startOf("quarter"),
      end: start.endOf("quarter"),
    };
  },
  half_year: (start) => {
    start = start || dayjs();
    const month = start.month();
    return month < 6
      ? {
          name: "Halbjahr",
          start: start.month(0).startOf("month"),
          end: start.month(5).endOf("month"),
        }
      : {
          name: "Halbjahr",
          start: start.month(6).startOf("month"),
          end: start.month(11).endOf("month"),
        };
  },
  year: (start) => {
    start = start || dayjs();
    return {
      name: "Jahr",
      start: start.startOf("year"),
      end: start.endOf("year"),
    };
  },
};

function adjustPeriod(period, startDate, direction, amount = 1) {
  const delta = direction === "next" ? amount : amount * -1;

  switch (period) {
    case "year":
      return startDate.add(delta, "year").startOf("year");

    case "half_year":
      return startDate.add(delta * 6, "month").startOf("month");

    case "quarter":
      return startDate.add(delta * 3, "month").startOf("quarter");

    case "month":
      return startDate.add(delta, "month").startOf("month");

    default:
      return startDate;
  }
}

function getPeriodTitle(period, startDate, endDate) {
  const year = startDate.year();

  switch (period) {
    case "year":
      return `Jahr ${year}`;

    case "half_year": {
      const half = startDate.month() < 6 ? 1 : 2;
      return `H${half} ${year}`;
    }

    case "quarter":
      return `Q${startDate?.quarter()} ${year}`;

    case "month":
      return format(startDate.toDate(), "LLLL yyyy", { locale: de });

    case "custom":
      return formatDatePeriod(startDate.toString(), endDate.toString(), {
        format: "short",
      });
    default:
      return "";
  }
}

export default function DatePeriodSelector({
  value,
  onChange,
  allowCustom = true,
  showTitle = true,
  allowNavigation = true,
}) {
  const [period, setPeriod] = useState(value?.period || "month");
  const [startDate, setStartDate] = useState(
    value?.startDate || PRESETS[value?.period || "month"]().start
  );
  const [endDate, setEndDate] = useState(
    value?.endDate || PRESETS[value?.period || "month"]().end
  );

  const title = getPeriodTitle(period, startDate, endDate);

  const datePickerInputProps = {
    textField: {
      size: "small",
      sx: {
        width: 136,

        "& .MuiOutlinedInput-input": {
          paddingLeft: "0.75rem",
        },

        // calendar icon button
        "& .MuiInputAdornment-root .MuiIconButton-root": {
          padding: "0.25rem",
        },

        // calendar icon itself
        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
          fontSize: 20,
        },
      },
    },
  };

  const periodTabs = useMemo(() => {
    const tabs = [];
    Object.entries(PRESETS).map(([key, value]) => {
      tabs.push({
        key: key,
        label: value().name,
      });
    });

    if (allowCustom) {
      tabs.push({
        key: "custom",
        label: "Zeitraum",
      });
    }

    return tabs;
  }, [allowCustom]);

  const emitChange = (newPeriod, newStart, newEnd) => {
    const newTitle = getPeriodTitle(newPeriod, newStart, newEnd);
    onChange?.({
      period: newPeriod,
      startDate: newStart,
      endDate: newEnd,
      title: newTitle,
    });
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (newPeriod !== "custom") {
      const { start, end } = PRESETS[newPeriod](startDate);
      setStartDate(start);
      setEndDate(end);
      emitChange(newPeriod, start, end);
    }
    else
    {
      emitChange(newPeriod, startDate, endDate);
    }
  };

  const handleStartDateChange = (newStart) => {
    setStartDate(newStart);
    emitChange(period, newStart, endDate);
  };

  const handleEndDateChange = (newEnd) => {
    setEndDate(newEnd);
    emitChange(period, startDate, newEnd);
  };

  const handlePrevPeriod = () => {
    const newStart = adjustPeriod(period, startDate, "prev");
    const { start, end } = PRESETS[period](newStart);
    setStartDate(start);
    setEndDate(end);
    emitChange(period, start, end);
  };

  const handleNextPeriod = () => {
    const newStart = adjustPeriod(period, startDate, "next");
    const { start, end } = PRESETS[period](newStart);
    setStartDate(start);
    setEndDate(end);
    emitChange(period, start, end);
  };

  const prevButton = () => (
    <IconButton disabled={period === "custom"} onClick={handlePrevPeriod}>
      <PrevIcon />
    </IconButton>
  );

  const nextButton = () => (
    <IconButton disabled={period === "custom"} onClick={handleNextPeriod}>
      <NextIcon fontSize="small" />
    </IconButton>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Stack spacing={2} direction={"row"}>
        <TabList
          tabs={periodTabs}
          activeTab={period}
          onTabChange={handlePeriodChange}
        />

        {/* prev / next period */}
        {allowNavigation && prevButton()}

        {/* period title */}
        {showTitle && (
          <Typography
            component={"div"}
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: typographyTokens.weight.semibold,
              textAlign: "center",
            }}
          >
            {getPeriodTitle(period, startDate, endDate)}
          </Typography>
        )}

        {/* if title, display next button after title */}
        {showTitle && allowNavigation && nextButton()}

        <Box display="flex" gap={2} sx={{ pt: 1 }}>
          <DatePicker
            label="Startdatum"
            value={startDate}
            onChange={handleStartDateChange}
            disabled={period !== "custom"}
            slotProps={datePickerInputProps}
          />
          <DatePicker
            label="Enddatum"
            value={endDate}
            onChange={handleEndDateChange}
            disabled={period !== "custom"}
            slotProps={datePickerInputProps}
          />
        </Box>

        {/* if no title, display next button after date pickers */}
        {!showTitle && allowNavigation && nextButton()}
      </Stack>
    </LocalizationProvider>
  );
}
